import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import constants from '@core/utils/constants';
import serializer from './serializer.js';

/**
 * DeliveryBookController V3
 * Gere tous les evenements deliverybook et les appels API
 */

// Store reference (sera initialisée au runtime)
let deliverybookStore = null;

// Flag pour éviter la double initialisation
let isInitialized = false;

// Helper pour finir le loading avec delai
function finishLoading() {
  setTimeout(() => {
    bus.emit(constants.EVENTS.LOADING.FINISH);
  }, 300);
}

// Helper pour démarrer le loading avec delai (éviter glitches)
async function startLoadingSmoothly() {
  bus.emit(constants.EVENTS.LOADING.START);
  // No artificial delay to show loading immediately
  // await new Promise((resolve) => setTimeout(resolve, 150));
}

// ============================================
// INITIALIZATION
// ============================================

/**
 * Initialise les ecouteurs d'evenements du controller
 */
function initController() {
  // Eviter la double initialisation
  if (isInitialized) {
    console.log('[DeliveryBookController] Already initialized, skipping');
    return;
  }

  console.log('[DeliveryBookController] Initializing deliverybook controller V3');
  isInitialized = true;

  // Get deliverybook store
  deliverybookStore = getStore('deliverybook');
  if (!deliverybookStore) {
    console.error('[DeliveryBookController] Deliverybook store not found');
    return;
  }

  // Enregistrer les ecouteurs d'evenements
  setupAddressesHandlers();
  setupAddressCreateHandlers();
  setupAddressUpdateHandlers();
  setupAddressDeleteHandlers();
  setupAddressRequestHandlers();
  setupAddressInviteHandlers();
  setupAddressDefaultHandlers();
  setupModalHandlers();
  setupTabHandlers();

  console.log('[DeliveryBookController] All event handlers registered successfully');
}

// ============================================
// ADDRESSES HANDLERS
// ============================================

function setupAddressesHandlers() {
  bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESSES_LOAD, loadAddresses);

  // Also listen for legacy V2 events
  bus.on('new_delivery_location_added', () => loadAddresses());
  bus.on('delivery_location_list_changed', () => loadAddresses());
}

/**
 * Charge les adresses depuis l'API
 */
async function loadAddresses() {
  const requestId = `loadAddresses-${Date.now()}`;

  console.log('[DeliveryBookController] Loading addresses');

  try {
    // Notifier le store que le chargement commence
    await startLoadingSmoothly();
    deliverybookStore.state.loading = true;
    deliverybookStore.state.error = null;
    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESSES_LOADING);

    // Get userId
    const userId = serializer.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Use V2 style dataKey for ListFetcher compatibility
    const dataKeys = ['deliveryLocations', 'userInvitations', 'locationRequests'];

    const endpoints = {
      deliveryLocations: `${constants.ENDPOINTS.DELIVERYBOOK.BASE}${constants.ENDPOINTS.DELIVERYBOOK.ADDRESSES_GET}&userId=${userId}`,
      userInvitations: `${constants.ENDPOINTS.DELIVERYBOOK.BASE}${constants.ENDPOINTS.DELIVERYBOOK.ADDRESS_INVITATIONS_GET}&userId=${userId}`,
      locationRequests: `${constants.ENDPOINTS.DELIVERYBOOK.BASE}${constants.ENDPOINTS.DELIVERYBOOK.ADDRESS_REQUESTS_GET}&senderId=${userId}`
    };

    // Load all three types in parallel
    const promises = dataKeys.map(key => {
      return new Promise((resolve) => {
        const reqId = `${key}-${Date.now()}`;

        bus.emit(constants.EVENTS.NETWORK.REQUEST, {
          id: reqId,
          dataKey: key,
          url: endpoints[key],
          method: 'GET',
          data: { userId } // Optional fallback
        });

        const handler = (res) => {
          if (res.id === reqId) {
            bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
            resolve({ key, data: res.data });
          }
        };
        bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
      });
    });

    console.log('[DeliveryBookController] Parallel loading started for:', dataKeys);
    const results = await Promise.all(promises);
    console.log('[DeliveryBookController] Parallel loading completed');

    const allAddresses = [];
    const invitations = [];
    const requests = [];
    const saved = [];

    results.forEach(({ key, data: responseBody }) => {
      if (responseBody?.statusCode == 200 && responseBody?.data) {
        const items = Array.isArray(responseBody.data) ? responseBody.data : [];

        // Mapper la clé API vers le sourceType du serializer
        const sourceTypeMap = {
          deliveryLocations: 'saved',
          userInvitations: 'invitation',
          locationRequests: 'request'
        };
        const sourceType = sourceTypeMap[key] || 'saved';

        items.forEach((item) => {
          const address = serializer.deserializeAddress(item, sourceType);
          if (!address) return;

          allAddresses.push(address);
          if (key === 'userInvitations') {
            invitations.push(address);
          } else if (key === 'locationRequests') {
            requests.push(address);
          } else {
            saved.push(address);
          }
        });
      }
    });

    // Mettre a jour le store via un seul evenement global
    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESSES_LOADED, {
      addresses: allAddresses,
      invitations,
      requests,
      saved
    });

    finishLoading();
  } catch (error) {
    console.error('[DeliveryBookController] Error loading addresses:', error);
    deliverybookStore.state.loading = false;
    const errorData = {
      message: error.message,
      code: 'ADDRESSES_LOAD_ERROR'
    };

    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur de chargement',
      body: 'Impossible de charger vos adresses. Veuillez verifier votre connexion.'
    });

    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESSES_LOAD_ERROR, errorData);
    finishLoading();
  }
}

// ============================================
// ADDRESS CREATE HANDLERS
// ============================================

function setupAddressCreateHandlers() {
  bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATE, createAddress);
}

/**
 * Cree une nouvelle adresse
 */
async function createAddress(addressData) {
  const requestId = `createAddress-${Date.now()}`;

  console.log('[DeliveryBookController] Creating address:', addressData);

  try {
    deliverybookStore.state.loading = true;
    deliverybookStore.state.error = null;
    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATING);
    await startLoadingSmoothly();

    const payload = serializer.serializeAddress(addressData);
    const userId = serializer.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    payload.userId = userId;

    console.log('[DeliveryBookController] Create address payload:', payload);

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      dataKey: constants.ENDPOINTS.addDeliveryLocation,
      url: `${constants.ENDPOINTS.DELIVERYBOOK.BASE}${constants.ENDPOINTS.DELIVERYBOOK.ADDRESS_CREATE}`,
      method: 'POST',
      data: payload
    });

    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        deliverybookStore.state.loading = false;

        if (res.success) {
          const addressData = serializer.deserializeAddressResponse(res.data);

          console.log('[DeliveryBookController] Address created:', addressData);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Adresse ajoutee',
            body: 'Votre adresse a ete ajoutee avec succes.'
          });

          bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATED, addressData);
        } else {
          const error = serializer.deserializeError(res.data);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur',
            body: error.message || 'Impossible d\'ajouter cette adresse. Veuillez reessayer.'
          });

          bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATE_ERROR, error);
        }

        finishLoading();
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[DeliveryBookController] Error creating address:', error);
    deliverybookStore.state.loading = false;
    const errorData = {
      message: error.message,
      code: 'ADDRESS_CREATE_ERROR'
    };

    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Une erreur est survenue. Veuillez verifier votre connexion et reessayer.'
    });

    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATE_ERROR, errorData);
    finishLoading();
  }
}

// ============================================
// ADDRESS UPDATE HANDLERS
// ============================================

function setupAddressUpdateHandlers() {
  bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_UPDATE, updateAddress);
}

async function updateAddress({ addressId, updates }) {
  const requestId = `updateAddress-${Date.now()}`;

  console.log('[DeliveryBookController] Updating address:', addressId, updates);

  try {
    deliverybookStore.state.loading = true;
    deliverybookStore.state.error = null;
    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_UPDATING);
    await startLoadingSmoothly();

    const payload = serializer.serializeAddress(updates);
    const userId = serializer.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    payload.userId = userId;
    payload.addressId = addressId;

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      dataKey: constants.ENDPOINTS.updateDeliveryLocation || 'updateDeliveryLocation',
      url: `${constants.ENDPOINTS.DELIVERYBOOK.BASE}${constants.ENDPOINTS.DELIVERYBOOK.ADDRESS_UPDATE}/${addressId}?group=ultraxeno`,
      method: 'POST', // The plan specified POST for deduced update
      data: payload
    });

    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        deliverybookStore.state.loading = false;

        if (res.success) {
          const addressData = serializer.deserializeAddressResponse(res.data);

          console.log('[DeliveryBookController] Address updated:', addressData);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Adresse modifiee',
            body: 'Votre adresse a ete mise a jour avec succes.'
          });

          bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_UPDATED, addressData);
        } else {
          const error = serializer.deserializeError(res.data);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur',
            body: error.message || 'Impossible de modifier cette adresse. Veuillez reessayer.'
          });

          bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_UPDATE_ERROR, error);
        }

        finishLoading();
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[DeliveryBookController] Error updating address:', error);
    deliverybookStore.state.loading = false;
    const errorData = {
      message: error.message,
      code: 'ADDRESS_UPDATE_ERROR'
    };

    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Une erreur est survenue. Veuillez reessayer.'
    });

    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_UPDATE_ERROR, errorData);
    finishLoading();
  }
}

// ============================================
// ADDRESS DELETE HANDLERS
// ============================================

function setupAddressDeleteHandlers() {
  bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_DELETE, deleteAddress);
}

async function deleteAddress(addressId) {
  const requestId = `deleteAddress-${Date.now()}`;

  console.log('[DeliveryBookController] Deleting address:', addressId);

  try {
    deliverybookStore.state.loading = true;
    deliverybookStore.state.error = null;
    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_DELETING);
    await startLoadingSmoothly();

    const userId = serializer.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Déterminer le bon endpoint en cherchant l'adresse dans chaque liste séparée.
    // On ne peut pas utiliser `address.locationRequestLink` car le serializer le
    // construisait autrefois pour tous les items — désormais seul le sourceType est fiable.
    let apiUrl;
    const isRequest = deliverybookStore.state.requests.some(a => a.id === addressId);
    const isInvitation = deliverybookStore.state.invitations.some(a => a.id === addressId);

    if (isRequest) {
      apiUrl = `${constants.ENDPOINTS.DELIVERYBOOK.BASE}${constants.ENDPOINTS.DELIVERYBOOK.ADDRESS_REQUEST_DELETE}/${addressId}?group=ultraxeno`;
    } else if (isInvitation) {
      apiUrl = `${constants.ENDPOINTS.DELIVERYBOOK.BASE}${constants.ENDPOINTS.DELIVERYBOOK.ADDRESS_INVITE_DELETE}/${addressId}?group=ultraxeno`;
    } else {
      // Adresse sauvegardée (saved)
      apiUrl = `${constants.ENDPOINTS.DELIVERYBOOK.BASE}${constants.ENDPOINTS.DELIVERYBOOK.ADDRESS_DELETE}/${addressId}?group=ultraxeno`;
    }

    console.log(`[DeliveryBookController] Deleting using URL:`, apiUrl);

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      dataKey: constants.ENDPOINTS.removeDeliveryLocation,
      url: apiUrl,
      method: 'POST'
    });

    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        deliverybookStore.state.loading = false;

        if (res.success) {
          console.log('[DeliveryBookController] Address deleted:', addressId);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Supprimé',
            body: 'L\'élément a été supprimé avec succès.'
          });

          bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_DELETED, { addressId });
        } else {
          const error = serializer.deserializeError(res.data);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur',
            body: error.message || 'Impossible de supprimer cet élément. Veuillez reessayer.'
          });

          bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_DELETE_ERROR, error);
        }

        finishLoading();
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[DeliveryBookController] Error deleting address:', error);
    deliverybookStore.state.loading = false;
    const errorData = {
      message: error.message,
      code: 'ADDRESS_DELETE_ERROR'
    };

    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Une erreur est survenue. Veuillez reessayer.'
    });

    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_DELETE_ERROR, errorData);
    finishLoading();
  }
}

// ============================================
// ADDRESS REQUEST HANDLERS
// ============================================

function setupAddressRequestHandlers() {
  bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATE, createAddressRequest);
}

async function createAddressRequest(requestData) {
  const requestId = `createAddressRequest-${Date.now()}`;

  console.log('[DeliveryBookController] Creating address request:', requestData);

  try {
    deliverybookStore.state.loading = true;
    deliverybookStore.state.error = null;
    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATING);
    await startLoadingSmoothly();

    const payload = serializer.serializeAddressRequest(requestData);
    const userId = serializer.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    payload.userId = userId;

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      dataKey: constants.ENDPOINTS.requestLocationInfo,
      url: `${constants.ENDPOINTS.DELIVERYBOOK.BASE}${constants.ENDPOINTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATE}`,
      method: 'POST',
      data: payload
    });

    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        deliverybookStore.state.loading = false;

        if (res.success) {
          const requestData = serializer.deserializeAddressResponse(res.data, 'request');

          console.log('[DeliveryBookController] Address request created:', requestData);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Demande creee',
            body: 'La demande d\'adresse a ete creee avec succes.'
          });

          bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATED, requestData);

          // Emit legacy V2 event for tab switching
          bus.emit(constants.EVENTS.DELIVERYBOOK.TAB_CHANGED, 'requests');
        } else {
          const error = serializer.deserializeError(res.data);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur',
            body: error.message || 'Impossible de creer la demande. Veuillez reessayer.'
          });

          bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATE_ERROR, error);
        }

        finishLoading();
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[DeliveryBookController] Error creating address request:', error);
    deliverybookStore.state.loading = false;
    const errorData = {
      message: error.message,
      code: 'ADDRESS_REQUEST_CREATE_ERROR'
    };

    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Une erreur est survenue. Veuillez reessayer.'
    });

    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATE_ERROR, errorData);
    finishLoading();
  }
}

// ============================================
// ADDRESS INVITE HANDLERS
// ============================================

function setupAddressInviteHandlers() {
  bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_INVITE_CREATE, createAddressInvite);
  // La suppression d'invitation passe par le handler unifié ADDRESS_DELETE
  // qui détecte automatiquement le bon endpoint via les listes séparées du store
}

async function createAddressInvite(inviteData) {
  const requestId = `createAddressInvite-${Date.now()}`;

  console.log('[DeliveryBookController] Creating address invite:', inviteData);

  try {
    deliverybookStore.state.loading = true;
    deliverybookStore.state.error = null;
    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_INVITE_CREATING);
    await startLoadingSmoothly();

    const payload = serializer.serializeAddressInvite(inviteData);
    const userId = serializer.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    payload.userId = userId;

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      dataKey: constants.ENDPOINTS.inviteContact,
      url: `${constants.ENDPOINTS.DELIVERYBOOK.BASE}${constants.ENDPOINTS.DELIVERYBOOK.ADDRESS_INVITE_CREATE}`,
      method: 'POST',
      data: payload
    });

    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        deliverybookStore.state.loading = false;

        if (res.success) {
          const inviteData = serializer.deserializeAddressResponse(res.data, 'invitation');

          console.log('[DeliveryBookController] Address invite created:', inviteData);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Invitation creee',
            body: 'L\'invitation a ete creee avec succes.'
          });

          bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_INVITE_CREATED, inviteData);

          // Emit legacy V2 event for tab switching
          bus.emit(constants.EVENTS.DELIVERYBOOK.TAB_CHANGED, 'invitations');
        } else {
          const error = serializer.deserializeError(res.data);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur',
            body: error.message || 'Impossible de creer l\'invitation. Veuillez reessayer.'
          });

          bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_INVITE_CREATE_ERROR, error);
        }

        finishLoading();
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[DeliveryBookController] Error creating address invite:', error);
    deliverybookStore.state.loading = false;
    const errorData = {
      message: error.message,
      code: 'ADDRESS_INVITE_CREATE_ERROR'
    };

    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Une erreur est survenue. Veuillez reessayer.'
    });

    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_INVITE_CREATE_ERROR, errorData);
    finishLoading();
  }
}

// ============================================
// MODAL HANDLERS
// ============================================

function setupModalHandlers() {
  // Open modal for cart/package selection (legacy V2 event)
  bus.on('delivery_location_selection_requested_from_book', (data) => {
    bus.emit(constants.EVENTS.DELIVERYBOOK.MODAL_OPEN, {
      isFromCart: true,
      requestKey: data?.requestKey || 'cart_delivery_location'
    });
  });

  // Open modal from profile (legacy V2 event)
  bus.on('deliverybook_page_requested', () => {
    bus.emit(constants.EVENTS.DELIVERYBOOK.MODAL_OPEN, {
      isFromCart: false,
      requestKey: null
    });
  });

  // Handle address selection
  bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_SELECT, (address) => {
    console.log('[DeliveryBookController] Address selected:', address);

    // Emit legacy V2 event for cart compatibility
    bus.emit('delivery_location_confirmed', {
      requestKey: deliverybookStore.state.requestKey,
      selectedLocation: {
        lat: address.location?.lat || 0,
        lng: address.location?.lng || 0,
        address: address.locationText || address.location?.address || '',
        city: address.location?.city || '',
        description: address.location?.description || '',
        contactName: address.name,
        contactPhone: address.phoneNumber
      }
    });

    // Close modal
    bus.emit(constants.EVENTS.DELIVERYBOOK.MODAL_CLOSE);
  });

  // Handle location confirmed (legacy V2 event)
  bus.on(constants.EVENTS.DELIVERYBOOK.LOCATION_CONFIRMED, (location) => {
    console.log('[DeliveryBookController] Location confirmed:', location);

    bus.emit('delivery_location_confirmed', {
      requestKey: deliverybookStore.state.requestKey,
      selectedLocation: location
    });

    bus.emit(constants.EVENTS.DELIVERYBOOK.MODAL_CLOSE);
  });
}

// ============================================
// TAB HANDLERS
// ============================================

function setupTabHandlers() {
  bus.on(constants.EVENTS.DELIVERYBOOK.TAB_CHANGE, (tab) => {
    console.log('[DeliveryBookController] Tab change requested:', tab);
    deliverybookStore.state.selectedTab = tab;
    bus.emit(constants.EVENTS.DELIVERYBOOK.TAB_CHANGED, tab);
  });
}

// ============================================
// ADDRESS DEFAULT HANDLERS
// ============================================

function setupAddressDefaultHandlers() {
  bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_SET_DEFAULT, setDefaultAddress);
}

async function setDefaultAddress({ id }) {
  const requestId = `setDefaultAddress-${Date.now()}`;

  console.log('[DeliveryBookController] Setting address as default:', id);

  try {
    deliverybookStore.state.loading = true;
    deliverybookStore.state.error = null;
    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_SETTING_DEFAULT);
    await startLoadingSmoothly();

    const userId = serializer.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const payload = { id, userId };

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      dataKey: 'setDefaultDeliveryLocation',
      url: `${constants.ENDPOINTS.DELIVERYBOOK.BASE}${constants.ENDPOINTS.DELIVERYBOOK.ADDRESS_SET_DEFAULT}`,
      method: 'POST',
      data: payload
    });

    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        deliverybookStore.state.loading = false;

        if (res.success) {
          console.log('[DeliveryBookController] Address set as default:', res.data);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Adresse par défaut',
            body: 'L\'adresse a été définie comme adresse par défaut avec succès.'
          });

          bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_DEFAULT_SET, { id });
          // Optionnel : recharger les adresses pour mettre à jour l'affichage
          loadAddresses();
        } else {
          const error = serializer.deserializeError(res.data);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur',
            body: error.message || 'Impossible de définir cette adresse comme défaut. Veuillez réessayer.'
          });

          bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_SET_DEFAULT_ERROR, error);
        }

        finishLoading();
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[DeliveryBookController] Error setting address as default:', error);
    deliverybookStore.state.loading = false;
    const errorData = {
      message: error.message,
      code: 'ADDRESS_SET_DEFAULT_ERROR'
    };

    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Une erreur est survenue. Veuillez réessayer.'
    });

    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_SET_DEFAULT_ERROR, errorData);
    finishLoading();
  }
}

// ============================================
// EXPORT
// ============================================

// Auto-initialiser le controller quand le store est disponible
bus.on(constants.EVENTS.APP.READY, () => {
  setTimeout(() => {
    initController();
  }, 100);
});

// Export des fonctions pour acces externe si necessaire
export const deliverybookController = {
  init: initController,
  loadAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  createAddressRequest,
  createAddressInvite,
  setDefaultAddress
};

export default deliverybookController;
